/***
 *
 *   ACCOUNT / DETAIL
 *   View the details of an individual account
 *
 **********/

import { useContext, useEffect, useState, Fragment } from 'react';
import { Animate, Chart, useAPI, useDebounce, Icon, Badge, Loader, BlankSlateMessage, LicenseBadge, AuthContext, ViewContext } from 'components/lib';
import { AddCreditsModal } from 'components/addCreditsModal/addCreditsModal';
import { PoolAccessToggle } from 'components/poolAccessToggle/poolAccessToggle';
import { convertToLocalTime } from 'utility/helper';
import { buildWebappUrl, recordWebappAudit } from 'utility/webapp';
import { rowsToCsv, downloadCsv } from 'utility/csv';
import {
  TABLE_CONTAINER, TH_STYLE, TD_STYLE, TAB_BAR_STYLE, tabStyle,
  MODAL_OVERLAY, MODAL_CARD, MODAL_HEADER_ROW, MODAL_TITLE, MODAL_CLOSE_BTN,
  MODAL_WARNING, MODAL_ERROR, MODAL_FOOTER_ROW, BTN_SECONDARY, btnDestructive, btnRowDestructive,
} from './detail.styles';
import { pushRecentlyViewed } from 'views/dashboard';
import { useNavigate, useParams } from 'react-router-dom';
import Axios from 'axios';

// ─── Sub-components ───────────────────────────────────────────────────────────

function ProfileCell({ icon, label, children }) {
  return (
    <div style={{ padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        {icon}
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontFamily: "'Open Sans', sans-serif",
        }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 14, color: '#111827', fontFamily: "'Open Sans', sans-serif" }}>
        {children}
      </div>
    </div>
  );
}

const LICENSE_COLORS = ['blue', 'purple', 'orange'];

// CORE-3993 — copy for removing the licences an account pays for but nobody
// uses. A support rep is changing someone else's bill, so every string names
// the count, the plan and the consequence rather than saying "are you sure?".
// The webapp's master-gated CS surface (CORE-3993), called with the rep's own
// session — the same channel addCreditsModal uses for /api/credit/admin/adjust.
const CLEANUP_ENDPOINT = '/api/licenses/admin/cleanup-unassigned';

const CLEANUP_COPY = {
  action: 'Remove unused',
  title: 'Remove Unused Licenses',
  confirm: 'Remove licenses',
  confirming: 'Removing…',
  cancel: 'Cancel',
  warning:
    'This reduces the quantity on the Stripe subscription — the account will be billed for fewer licenses from its next invoice.',
  nothingToRemove: 'Every license on this plan is assigned.',
  describe: ({ count, plan }) => `Remove ${count} unused license${count === 1 ? '' : 's'} from ${plan}?`,
  succeeded: ({ count, plan }) => `Removed ${count} unused license${count === 1 ? '' : 's'} from ${plan}.`,
  failed: 'Something went wrong',
};

function LicenseTags({ raw }) {
  if (!raw) return <span style={{ color: '#9ca3af' }}>—</span>;
  const all = raw.split(',').map((s) => s.trim()).filter(Boolean);
  const visible = all.slice(0, 3);
  const overflow = all.length - 3;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {visible.map((tag, i) => (
        <Badge key={tag} text={tag} color={LICENSE_COLORS[i % LICENSE_COLORS.length]} />
      ))}
      {overflow > 0 && (
        <Badge text={`+${overflow}`} color="blue" />
      )}
    </div>
  );
}

function AvgPromptBadge({ value }) {
  if (value === undefined || value === null) return <span style={{ color: '#9ca3af' }}>—</span>;
  const n = Number(value);
  const color = n >= 50 ? '#16a34a' : n >= 10 ? '#ca8a04' : '#dc2626';
  return (
    <span style={{ fontSize: 14, fontWeight: 700, color, fontFamily: "'Open Sans', sans-serif" }}>
      {n}
    </span>
  );
}

function DateTimeValue({ iso }) {
  if (!iso) return <span style={{ color: '#9ca3af' }}>—</span>;
  const local = convertToLocalTime(iso);
  const parts = typeof local === 'string' ? local.split(' ') : [String(local)];
  // Expect "DD/MM/YYYY HH:MM:SS" — split on first space
  const date = parts[0] || '';
  const time = parts.slice(1).join(' ') || '';
  return (
    <span style={{ fontFamily: "'Open Sans', sans-serif" }}>
      <span style={{ color: '#111827', fontWeight: 500 }}>{date}</span>
      {time && (
        <span style={{ color: '#9ca3af', marginLeft: 4, fontSize: 12 }}>{time}</span>
      )}
    </span>
  );
}

// Table shared style helpers — see detail.styles.js

function SimpleTable({ columns, rows, onRowClick, sort, onSortChange }) {
  if (!rows || rows.length === 0) {
    return (
      <div style={{ ...TABLE_CONTAINER, padding: '32px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
        No data
      </div>
    );
  }
  return (
    <div style={TABLE_CONTAINER}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={TH_STYLE}>
                {col.sortKey && onSortChange ? (
                  <button
                    onClick={() => onSortChange(col.sortKey)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit' }}
                  >
                    {col.label}
                    <span style={{ fontSize: 10, color: sort?.key === col.sortKey ? '#1974f9' : '#d1d5db' }}>
                      {sort?.key === col.sortKey ? (sort.dir === 'desc' ? '↓' : '↑') : '⇅'}
                    </span>
                  </button>
                ) : col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{
                borderBottom: '1px solid #f3f4f6',
                cursor: onRowClick ? 'pointer' : 'default',
                transition: 'background 0.1s',
              }}
              onMouseEnter={(e) => { if (onRowClick) e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={(e) => { if (onRowClick) e.currentTarget.style.background = ''; }}
            >
              {columns.map((col) => (
                <td key={col.key} style={{ ...TD_STYLE, color: col.dark ? '#111827' : '#374151', fontWeight: col.dark ? 500 : 400 }}>
                  {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Intercom Conversations (for account) ────────────────────────────────────

const CONV_STATE_BADGE_COLOR = { open: 'orange', snoozed: 'purple', closed: 'green' };

function convRelative(ts) {
  if (!ts) return '—';
  const diff = Date.now() - ts * 1000;
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 2)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function AccountConversations({ email }) {
  const { data, loading, error } = useAPI(
    email ? `/api/intercom/conversations/by-contact?email=${encodeURIComponent(email)}` : null
  );

  const conversations = data?.conversations || [];

  if (!email)
    return <BlankSlateMessage title="No email" text="No email on this account — cannot look up Intercom conversations." />;

  if (loading)
    return <Loader />;

  if (error)
    return <BlankSlateMessage title="Failed to load" text="Failed to load Intercom conversations." />;

  if (conversations.length === 0)
    return <BlankSlateMessage title="No conversations" text={`No Intercom conversations found for ${email}.`} />;

  return (
    <div style={TABLE_CONTAINER}>
      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 90px 80px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        {['Subject', 'Assignee', 'State', 'Updated'].map((h, i) => (
          <div key={h} style={{ ...TH_STYLE, textAlign: i === 3 ? 'right' : undefined }}>{h}</div>
        ))}
      </div>
      {/* Rows */}
      {conversations.map((conv) => (
        <a
          key={conv.id}
          href={conv.conversation_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'grid', gridTemplateColumns: '1fr 160px 90px 80px', alignItems: 'center', borderBottom: '1px solid #f3f4f6', textDecoration: 'none', color: 'inherit' }}
          onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
          onMouseLeave={e => e.currentTarget.style.background = ''}
        >
          <div style={{ ...TD_STYLE, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {conv.title}
            </div>
          </div>
          <div style={{ ...TD_STYLE, fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {conv.assignee || <span style={{ color: '#d1d5db' }}>Unassigned</span>}
          </div>
          <div style={TD_STYLE}><Badge text={conv.state} color={CONV_STATE_BADGE_COLOR[conv.state] || 'blue'} /></div>
          <div style={{ ...TD_STYLE, fontSize: 12, color: '#9ca3af', textAlign: 'right' }}>{convRelative(conv.updated_at)}</div>
        </a>
      ))}
    </div>
  );
}

// ─── FetchEvents (unchanged) ──────────────────────────────────────────────────

function FetchEvents({ id, setData }) {
  const eventCategoryOptions = useAPI(`/api/event?group=name&account_id=${id}`);

  useEffect(() => {
    if (eventCategoryOptions.data)
      setData(
        eventCategoryOptions?.data?.results.map((option) => ({
          label: option.name,
          value: option.name,
        }))
      );
  }, [eventCategoryOptions.data, setData]);

  return false;
}

// ─── Main Component ───────────────────────────────────────────────────────────

const TABS = ['Profile', 'Purchased Plans', 'Credits', 'Assigned Plans User List', 'User List', 'Events', 'Conversations'];

const TAB_SLUGS = {
  'Profile': 'profile',
  'Purchased Plans': 'purchased-plans',
  'Credits': 'credits',
  'Assigned Plans User List': 'assigned-plans',
  'User List': 'user-list',
  'Events': 'events',
  'Conversations': 'conversations',
};
const SLUG_TO_TAB = Object.fromEntries(Object.entries(TAB_SLUGS).map(([k, v]) => [v, k]));

export function AccountDetail() {
  const limit = 25;
  const navigate = useNavigate();
  const viewContext = useContext(ViewContext);
  const authContext = useContext(AuthContext);
  const { id, tab: tabParam } = useParams();

  // redirect /accounts/:id → /accounts/:id/profile (also canonicalise unknown slugs)
  useEffect(() => {
    if (!tabParam || !SLUG_TO_TAB[tabParam]) navigate(`/accounts/${id}/profile`, { replace: true });
  }, [id, tabParam, navigate]);

  // active tab derived from URL path param
  const activeTab = SLUG_TO_TAB[tabParam] || 'Profile';

  function handleTabChange(tab) {
    navigate(`/accounts/${id}/${TAB_SLUGS[tab]}`);
  }

  // credits tab: members table sort (defaults to most-recent-use first)
  const [memberSort, setMemberSort] = useState({ key: 'lastCreditUse', dir: 'desc' });
  function handleMemberSortChange(key) {
    setMemberSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));
  }

  // credits tab: ledger feed search/filter/pagination
  const LEDGER_LIMIT = 20;
  const [ledgerOffset, setLedgerOffset] = useState(0);
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledgerEntryType, setLedgerEntryType] = useState('');
  const [ledgerCycle, setLedgerCycle] = useState('all');
  const [exportingLedger, setExportingLedger] = useState(false);
  const [ledgerExportNote, setLedgerExportNote] = useState('');

  // event/prompts state
  const [eventName, setEventName] = useState('prompt_submitted');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [eventCategories, setEventCategories] = useState([]);
  const [chart, setChart] = useState(false);

  // fetch
  const account = useAPI(`/api/account/${id}`);
  const users = useAPI(`/api/user?account_id=${id}`);
  const purchasedPlans = useAPI(`/api/account/${id}/plans`);
  const assignedPlans = useAPI(`/api/account/${id}/assigned-plans`);
  const onCreditsTab = activeTab === 'Credits';
  const creditBalance = useAPI(onCreditsTab ? `/api/credit/account/${id}/balance` : null);
  const creditBundles = useAPI(onCreditsTab ? `/api/credit/account/${id}/bundles` : null);
  const creditUsers = useAPI(onCreditsTab ? `/api/credit/account/${id}/users` : null);
  const creditTopup = useAPI(onCreditsTab ? `/api/credit/account/${id}/topup-config` : null);
  const creditCycle = useAPI(onCreditsTab ? `/api/credit/account/${id}/cycle` : null);
  const debouncedLedgerSearch = useDebounce(ledgerSearch, 400);
  const ledgerParams = new URLSearchParams({ offset: String(ledgerOffset), limit: String(LEDGER_LIMIT) });
  if (debouncedLedgerSearch.trim()) ledgerParams.set('search', debouncedLedgerSearch.trim());
  if (ledgerEntryType) ledgerParams.set('entryType', ledgerEntryType);
  if (ledgerCycle !== 'all') ledgerParams.set('cycle', ledgerCycle);
  const creditLedger = useAPI(onCreditsTab ? `/api/credit/account/${id}/ledger?${ledgerParams.toString()}` : null);
  const events = useAPI(`/api/event?&offset=${offset}&name=${eventName}&account_id=${id}`);

  useEffect(() => {
    if (events.data?.list) setTotal(events.data.list.total);
    if (events.data?.chart) setChart(events.data.chart);
  }, [events.data]);

  useEffect(() => {
    if (account.data?.length) {
      const a = account.data[0];
      pushRecentlyViewed({
        id,
        name: a.name || a.email || id,
        type: 'account',
      });
    }
  }, [account.data, id]);

  const accountData = account.data?.[0];
  const accountName = accountData?.name || '…';

  // editable profile fields
  const [ssoOnly, setSsoOnly] = useState(false);
  const [accountType, setAccountType] = useState('standard');
  const [learnFromQuestions, setLearnFromQuestions] = useState('true');

  useEffect(() => {
    if (accountData) {
      setSsoOnly(accountData.sso_only === true || accountData.sso_only === 'true' ? 'true' : 'false');
      setAccountType(accountData.type || 'standard');
      setLearnFromQuestions(accountData.learn_from_questions === false ? 'false' : 'true');
    }
  }, [accountData]);

  async function patchAccount(field, value) {
    try {
      await Axios.patch(`/api/account/${accountData.id}`, { [field]: value });
    } catch (err) {
      // revert on error
      if (field === 'sso_only') setSsoOnly(accountData.sso_only === true || accountData.sso_only === 'true' ? 'true' : 'false');
      if (field === 'type') setAccountType(accountData.type || 'standard');
      if (field === 'learn_from_questions') setLearnFromQuestions(accountData.learn_from_questions === false ? 'false' : 'true');
    }
  }

  // owner transfer
  const [ownerEmail, setOwnerEmail] = useState(null);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [newOwnerEmail, setNewOwnerEmail] = useState('');
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [ownerError, setOwnerError] = useState(null);
  const [ownerSaved, setOwnerSaved] = useState(false);

  // unassign modal
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [unassignTarget, setUnassignTarget] = useState(null);
  const [unassignSelected, setUnassignSelected] = useState({});
  const [unassignLoading, setUnassignLoading] = useState(false);
  const [unassignError, setUnassignError] = useState(null);

  // assign modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignSelected, setAssignSelected] = useState({});
  const [assignUserId, setAssignUserId] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState(null);

  // unused-license cleanup modal — cleanupTarget doubles as the open/closed
  // signal, so there is no second flag to keep in step with it.
  const [cleanupTarget, setCleanupTarget] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupError, setCleanupError] = useState(null);

  useEffect(() => {
    if (accountData?.email) setOwnerEmail(accountData.email);
  }, [accountData]);

  async function handleChangeOwner() {
    setOwnerLoading(true);
    setOwnerError(null);
    try {
      const res = await Axios.post(`/api/account/${id}/owner`, { email: newOwnerEmail });
      setOwnerEmail(res.data.email);
      setShowOwnerModal(false);
      setOwnerSaved(true);
      setTimeout(() => setOwnerSaved(false), 3000);
    } catch (err) {
      setOwnerError(err.response?.data?.message || 'Something went wrong');
    } finally {
      setOwnerLoading(false);
    }
  }

  function openUnassignModal(row) {
    setUnassignTarget(row);
    const initial = {};
    (row.licenses || []).forEach((l) => { initial[l.account_plans_id] = true; });
    setUnassignSelected(initial);
    setUnassignError(null);
    setShowUnassignModal(true);
  }

  async function handleUnassign() {
    setUnassignLoading(true);
    setUnassignError(null);
    try {
      const selectedIds = Object.entries(unassignSelected).filter(([, v]) => v).map(([k]) => k);
      await Axios.post(`/api/account/${id}/licenses/unassign`, {
        user_id: unassignTarget.user_id,
        account_plans_ids: selectedIds,
      });
      setShowUnassignModal(false);
      assignedPlans.refetch();
    } catch (err) {
      setUnassignError(err.response?.data?.message || 'Something went wrong');
    } finally {
      setUnassignLoading(false);
    }
  }

  function openAssignModal() {
    setAssignSelected({});
    setAssignUserId('');
    setAssignError(null);
    setShowAssignModal(true);
  }

  async function handleAssign() {
    setAssignLoading(true);
    setAssignError(null);
    try {
      const selectedIds = Object.entries(assignSelected).filter(([, v]) => v).map(([k]) => k);
      await Axios.post(`/api/account/${id}/licenses/assign`, {
        user_id: assignUserId,
        account_plans_ids: selectedIds,
      });
      setShowAssignModal(false);
      assignedPlans.refetch();
    } catch (err) {
      setAssignError(err.response?.data?.message || 'Something went wrong');
    } finally {
      setAssignLoading(false);
    }
  }

  function openCleanupModal(plan) {
    setCleanupError(null);
    setCleanupTarget(plan);
  }

  async function handleCleanup() {
    setCleanupLoading(true);
    setCleanupError(null);

    const url = buildWebappUrl(authContext.remote_server, CLEANUP_ENDPOINT);
    const auditBase = {
      action: 'license.cleanup',
      target_type: 'account_plan',
      target_id: cleanupTarget.id,
      account_id: id,
      method: 'POST',
      endpoint: url,
    };

    try {
      const res = await Axios({
        method: 'POST',
        url,
        data: { accountId: id, accountPlanId: cleanupTarget.id },
      });
      recordWebappAudit({ ...auditBase, status: res.status });
      const { data } = res;
      // Report what the webapp actually removed, not what we asked for: the
      // counts here are a snapshot, and an assignment may have landed since.
      viewContext.notification.show(
        CLEANUP_COPY.succeeded({
          count: data?.data?.removedUnassigned ?? cleanupTarget.available,
          plan: cleanupTarget.license,
        }),
        'success',
        true
      );
      setCleanupTarget(null);
      purchasedPlans.refetch();
      // Assignments cannot change here — only unassigned capacity is removed —
      // but the counts this tab shows are a snapshot, so re-read them in case
      // an assign/unassign landed while the modal was open.
      assignedPlans.refetch();
    } catch (err) {
      // A refused attempt to change a customer's billing is as audit-relevant
      // as a successful one.
      recordWebappAudit({ ...auditBase, status: err.response?.status ?? 0 });
      setCleanupError(err.response?.data?.message || CLEANUP_COPY.failed);
    } finally {
      setCleanupLoading(false);
    }
  }

  // ── Assigned Plans rows ────────────────────────────────────────────────────

  const assignedRows = (assignedPlans.data || []).map((plan) => ({
    ...plan,
    assigned_time: convertToLocalTime(plan.assigned_time),
  }));

  // license availability (for summary bar + assign modal)
  const assignedCountByPlan = {};
  (assignedPlans.data || []).forEach((user) => {
    (user.licenses || []).forEach((lic) => {
      assignedCountByPlan[lic.account_plans_id] = (assignedCountByPlan[lic.account_plans_id] || 0) + 1;
    });
  });
  const purchasedWithAvail = (purchasedPlans.data || []).map((plan) => ({
    ...plan,
    assigned: assignedCountByPlan[plan.id] || 0,
    available: plan.quantity - (assignedCountByPlan[plan.id] || 0),
  }));
  const totalPurchased = purchasedWithAvail.reduce((s, p) => s + p.quantity, 0);
  const totalAssigned = purchasedWithAvail.reduce((s, p) => s + p.assigned, 0);
  const totalAvailable = totalPurchased - totalAssigned;
  const availableLicenses = purchasedWithAvail.filter((p) => p.available > 0);

  // ── Purchased Plans rows ───────────────────────────────────────────────────
  // Derived from purchasedWithAvail so the tab shows what is actually idle —
  // the number the cleanup action acts on.

  const purchasedRows = purchasedWithAvail.map((plan) => ({
    ...plan,
    purchased_time: convertToLocalTime(plan.purchased_time),
  }));

  // ── Credits tab derived values ─────────────────────────────────────────────

  const CREDIT_STATUS_LABEL = { active: 'Active', exhausted: 'Depleted', expired: 'Expired' };

  const creditRemaining = creditBalance.data?.accountRemaining ?? 0;
  const creditDeficit = creditBalance.data?.accountDeficit ?? 0;
  const creditAvailable = creditRemaining - creditDeficit;
  const creditPurchased = (creditBundles.data || []).reduce((s, lot) => s + lot.creditsGranted, 0);
  const creditSpent = creditBalance.data?.accountSpent ?? 0;
  const poolAccessGranted = (creditUsers.data || []).filter((u) => u.isAccountPoolEnabled).length;
  const poolAccessTotal = (creditUsers.data || []).length;

  const formatPrice = (minorUnits, currency) =>
    minorUnits == null || !currency ? null : `${currency.toUpperCase()} ${(minorUnits / 100).toFixed(2)}`;

  const bundleRows = (creditBundles.data || []).map((lot) => ({
    ...lot,
    statusLabel: CREDIT_STATUS_LABEL[lot.status] || lot.status,
    purchasedDate: lot.landedAt ? convertToLocalTime(lot.landedAt).slice(0, 10) : '—',
    expiresDate: lot.expiresAt ? convertToLocalTime(lot.expiresAt).slice(0, 10) : '—',
    displayRemaining: lot.status === 'expired' ? 0 : lot.remaining,
    priceLabel: formatPrice(lot.priceMinorUnits, lot.currency),
    attributionEmail: lot.purchasedByEmail || lot.operatorEmail || null,
  }));

  const LEDGER_ENTRY_TYPE_LABEL = {
    grant: 'Grant', spend: 'Spend', expire: 'Expire', refund: 'Refund',
    adjustment: 'Adjustment', void: 'Void', task_refused: 'Task refused',
  };
  const ledgerTotal = creditLedger.data?.total ?? 0;
  const ledgerRows = (Array.isArray(creditLedger.data?.entries) ? creditLedger.data.entries : []).map((entry) => ({
    ...entry,
    entryTypeLabel: LEDGER_ENTRY_TYPE_LABEL[entry.entryType] || entry.entryType,
  }));

  const LEDGER_CSV_COLUMNS = [
    { key: 'timestampLabel', label: 'Timestamp' },
    { key: 'entryTypeLabel', label: 'Event' },
    { key: 'eventLabel', label: 'Detail' },
    { key: 'userLabel', label: 'User' },
    { key: 'drawnFrom', label: 'Drawn From' },
    { key: 'amount', label: 'Change' },
    { key: 'poolAfter', label: 'Pool After' },
  ];
  const LEDGER_EXPORT_LIMIT = 500;

  async function exportLedgerCsv() {
    setExportingLedger(true);
    setLedgerExportNote('');
    try {
      const params = new URLSearchParams(ledgerParams);
      params.set('limit', String(LEDGER_EXPORT_LIMIT));
      params.set('offset', '0');
      const res = await Axios.get(`/api/credit/account/${id}/ledger?${params.toString()}`);
      const { entries, total } = res.data.data;
      const csvRows = entries.map((entry) => ({
        ...entry,
        entryTypeLabel: LEDGER_ENTRY_TYPE_LABEL[entry.entryType] || entry.entryType,
        userLabel: entry.user ? `${entry.user.name} <${entry.user.email}>` : 'system',
        timestampLabel: convertToLocalTime(entry.createdAt),
      }));
      downloadCsv(`account-${id}-ledger.csv`, rowsToCsv(csvRows, LEDGER_CSV_COLUMNS));
      setLedgerExportNote(total > LEDGER_EXPORT_LIMIT ? `Exported the first ${LEDGER_EXPORT_LIMIT} of ${total} rows` : '');
    } finally {
      setExportingLedger(false);
    }
  }

  const memberRows = [...(creditUsers.data || [])].sort((a, b) => {
    if (memberSort.key !== 'lastCreditUse') return 0;
    const av = a.lastCreditUse ? new Date(a.lastCreditUse).getTime() : null;
    const bv = b.lastCreditUse ? new Date(b.lastCreditUse).getTime() : null;
    if (av === null && bv === null) return 0;
    if (av === null) return 1; // nulls always last, regardless of direction
    if (bv === null) return -1;
    return memberSort.dir === 'desc' ? bv - av : av - bv;
  });

  // ── User List rows ─────────────────────────────────────────────────────────

  const userRows = (users.data || []).map((user) => ({
    ...user,
    date_created: convertToLocalTime(user.date_created),
    last_active: convertToLocalTime(user.last_active),
  }));

  // ── Profile fields definition ──────────────────────────────────────────────
  // Each entry: { icon, label, render } | null (empty cell). Grouped into rows of 3.

  const SELECT_STYLE = { fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 4, padding: '2px 6px', background: '#fff', color: '#374151', cursor: 'pointer' };

  const profileFields = [
    {
      icon: 'list', label: 'ID',
      render: () => <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#374151' }}>{accountData?.id ?? '—'}</span>,
    },
    {
      icon: 'user', label: 'Name',
      render: () => accountData?.name || '—',
    },
    {
      icon: 'mail', label: 'Owner Email',
      render: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{ownerEmail || '—'}</span>
          {ownerSaved && <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>Saved ✓</span>}
          {!ownerSaved && ownerEmail && (
            <button
              onClick={() => { setNewOwnerEmail(''); setOwnerError(null); setShowOwnerModal(true); }}
              style={{ fontSize: 12, color: '#1974f9', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: "'Open Sans', sans-serif" }}
            >
              Change
            </button>
          )}
        </div>
      ),
    },
    {
      icon: 'file-text', label: 'Plan',
      render: () => accountData?.plan || '—',
    },
    {
      icon: 'zap', label: 'Active',
      render: () => accountData?.active == null
        ? <span style={{ color: '#9ca3af' }}>—</span>
        : accountData.active
          ? <span style={{ color: '#16a34a', fontWeight: 600 }}>True</span>
          : <span style={{ color: '#dc2626', fontWeight: 600 }}>False</span>,
    },
    {
      icon: 'calendar', label: 'Date Created',
      render: () => <DateTimeValue iso={accountData?.date_created} />,
    },
    {
      icon: 'credit-card', label: 'Licenses',
      render: () => <LicenseTags raw={accountData?.licenses} />,
    },
    {
      icon: 'message-square', label: 'Latest Prompt/mo',
      render: () => accountData?.latest_prompts_per_month ?? '—',
    },
    {
      icon: 'bar-chart-2', label: 'Avg Prompt/mo',
      render: () => <AvgPromptBadge value={accountData?.average_prompts_per_month} />,
    },
    {
      icon: 'zap', label: 'SSO Only',
      render: () => (
        <select value={ssoOnly} onChange={(e) => { const val = e.target.value; setSsoOnly(val); patchAccount('sso_only', val === 'true'); }} style={SELECT_STYLE}>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ),
    },
    {
      icon: 'file-text', label: 'Type',
      render: () => (
        <select value={accountType} onChange={(e) => { const val = e.target.value; setAccountType(val); patchAccount('type', val); }} style={SELECT_STYLE}>
          <option value="enterprise">enterprise</option>
          <option value="standard">standard</option>
        </select>
      ),
    },
    {
      icon: 'book-open', label: 'Learn From Questions',
      render: () => (
        <select aria-label="Learn From Questions" value={learnFromQuestions} onChange={(e) => { const val = e.target.value; setLearnFromQuestions(val); patchAccount('learn_from_questions', val === 'true'); }} style={SELECT_STYLE}>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ),
    },
    {
      icon: 'credit-card', label: 'Stripe Subscription ID',
      render: () => accountData?.stripe_subscription_id
        ? <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#374151' }}>{accountData.stripe_subscription_id}</span>
        : <span style={{ color: '#9ca3af' }}>—</span>,
    },
  ];

  return (
    <Animate>
      <FetchEvents key={id} id={id} setData={setEventCategories} />

      {/* ── Header ── */}
      <div style={{ marginBottom: 28, fontFamily: "'Open Sans', sans-serif" }}>
        <h1 style={{
          fontSize: 22,
          fontWeight: 700,
          color: '#111827',
          margin: '0 0 4px',
          lineHeight: 1.3,
        }}>
          <button
            onClick={() => navigate('/accounts')}
            style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 700, color: '#6b7280', cursor: 'pointer' }}
          >
            Customer Accounts
          </button>
          {' / '}
          {accountName}
        </h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
          Manage and monitor individual customer account
        </p>
      </div>

      {/* ── Tab bar ── */}
      <div style={TAB_BAR_STYLE}>
        {TABS.map((tab) => (
          <button
            key={tab}
            style={tabStyle(tab, activeTab)}
            onClick={() => handleTabChange(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ── Profile tab ── */}
      {activeTab === 'Profile' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 0, overflow: 'hidden', fontFamily: "'Open Sans', sans-serif" }}>
          {profileFields.reduce((rows, field, i) => {
            if (i % 3 === 0) rows.push([]);
            rows[rows.length - 1].push(field);
            return rows;
          }, []).map((row, rowIdx) => (
            <Fragment key={rowIdx}>
              {rowIdx > 0 && <div style={{ height: 1, background: '#f3f4f6' }} />}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
                {row.map((field, colIdx) => {
                  if (!field) return <div key={colIdx} />;
                  const cell = (
                    <ProfileCell icon={<Icon image={field.icon} color="grey" size={14} />} label={field.label}>
                      {field.render()}
                    </ProfileCell>
                  );
                  return colIdx === 1
                    ? <div key={colIdx} style={{ borderLeft: '1px solid #f3f4f6', borderRight: '1px solid #f3f4f6' }}>{cell}</div>
                    : <Fragment key={colIdx}>{cell}</Fragment>;
                })}
              </div>
            </Fragment>
          ))}
        </div>
      )}

      {/* ── Purchased Plans tab ── */}
      {activeTab === 'Purchased Plans' && (
        <SimpleTable
            columns={[
              { key: 'license', label: 'License', dark: true },
              { key: 'quantity', label: 'Purchased' },
              { key: 'assigned', label: 'Assigned' },
              {
                key: 'available',
                label: 'Unused',
                render: (available) => (
                  <span style={{ fontWeight: available > 0 ? 700 : 400, color: available > 0 ? '#b45309' : '#374151' }}>
                    {available}
                  </span>
                ),
              },
              { key: 'purchased_time', label: 'Purchased Time' },
              {
                key: 'id',
                label: '',
                render: (_, plan) => {
                  const hasUnused = plan.available > 0;
                  return (
                    <button
                      onClick={() => openCleanupModal(plan)}
                      disabled={!hasUnused}
                      title={hasUnused ? undefined : CLEANUP_COPY.nothingToRemove}
                      style={btnRowDestructive(hasUnused)}
                    >
                      {CLEANUP_COPY.action}
                    </button>
                  );
                },
              },
            ]}
          rows={purchasedRows}
        />
      )}

      {/* ── Credits tab ── */}
      {activeTab === 'Credits' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'Open Sans', sans-serif" }}>
          {/* Summary strip */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
            padding: '12px 20px',
          }}>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              <span style={{ fontWeight: 600, color: '#111827' }}>{creditPurchased.toLocaleString()}</span> purchased
              {' · '}
              <span style={{ fontWeight: 600, color: '#111827' }}>{creditSpent.toLocaleString()}</span> spent
              {' · '}
              <span style={{ fontWeight: 600, color: creditAvailable < 0 ? '#dc2626' : '#111827' }}>{creditAvailable.toLocaleString()}</span> available
              {' · '}
              pool access for <span style={{ fontWeight: 600, color: '#111827' }}>{poolAccessGranted} of {poolAccessTotal}</span> users
            </span>
            <button
              onClick={() => viewContext.modal.show({
                title: 'Add Credits',
                wide: true,
                content: (
                  <AddCreditsModal
                    accountId={id}
                    accountName={accountName}
                    accountDeficit={creditDeficit}
                    activeLots={(creditBundles.data || []).filter(l => l.status === 'active' && l.remaining > 0)}
                    users={creditUsers.data || []}
                    defaultPool="account"
                  />
                ),
              }, () => {
                creditBalance.refetch();
                creditBundles.refetch();
                creditUsers.refetch();
                creditLedger.refetch();
              })}
              style={{
                padding: '6px 14px', fontSize: 13, fontWeight: 600, borderRadius: 6, flex: 'none',
                border: '1px solid #1974f9', background: '#1974f9', color: '#fff',
                cursor: 'pointer', fontFamily: "'Open Sans', sans-serif",
              }}
            >
              Add Credits
            </button>
          </div>

          {/* Negative-pool warning banner */}
          {creditDeficit > 0 && (
            <div style={{
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
              padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <Icon image="alert-triangle" color="red" size={18} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#dc2626' }}>
                  Workspace pool is negative — new credit tasks are being refused
                </div>
              </div>
              <button
                onClick={() => viewContext.modal.show({
                  title: 'Add Credits',
                wide: true,
                  content: (
                    <AddCreditsModal
                      accountId={id}
                      accountName={accountName}
                      accountDeficit={creditDeficit}
                      activeLots={(creditBundles.data || []).filter(l => l.status === 'active' && l.remaining > 0)}
                      users={creditUsers.data || []}
                      defaultPool="account"
                      defaultAmount={creditDeficit}
                      defaultReasonCode="correction"
                    />
                  ),
                }, () => {
                  creditBalance.refetch();
                  creditBundles.refetch();
                  creditUsers.refetch();
                  creditLedger.refetch();
                })}
                style={{
                  padding: '6px 14px', fontSize: 13, fontWeight: 600, borderRadius: 6,
                  border: '1px solid #1974f9', background: '#1974f9', color: '#fff',
                  cursor: 'pointer', flex: 'none', fontFamily: "'Open Sans', sans-serif",
                }}
              >
                Settle balance
              </button>
            </div>
          )}

          {/* Six-card grid */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
            {[
              [
                {
                  icon: 'credit-card', label: 'Workspace Pool Balance',
                  render: () => (
                    <span style={{ fontSize: 18, fontWeight: 700, color: creditAvailable < 0 ? '#dc2626' : '#111827' }}>
                      {creditAvailable.toLocaleString()} <span style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af' }}>credits</span>
                    </span>
                  ),
                },
                {
                  icon: 'zap', label: 'Auto Top-Up',
                  render: () => creditTopup.data?.enabled
                    ? <span><span style={{ color: '#16a34a', fontWeight: 600 }}>on</span> <span style={{ color: '#9ca3af', fontSize: 12 }}>threshold {creditTopup.data.threshold ?? '—'} · {creditTopup.data.bundleId ?? 'no bundle set'}</span></span>
                    : <span style={{ color: '#dc2626', fontWeight: 600 }}>off</span>,
                },
                {
                  icon: 'credit-card', label: 'Payment Method',
                  render: () => <span style={{ color: '#9ca3af' }}>Not available in Mission Control yet</span>,
                },
              ],
              [
                {
                  icon: 'calendar', label: 'Billing Cycle',
                  render: () => creditCycle.data?.cycleStart
                    ? <span>{convertToLocalTime(creditCycle.data.cycleStart).slice(0, 10)} → {convertToLocalTime(creditCycle.data.cycleEnd).slice(0, 10)}</span>
                    : <span style={{ color: '#9ca3af' }}>No active billing cycle</span>,
                },
                {
                  icon: 'bar-chart-2', label: 'Consumed This Cycle',
                  render: () => (
                    <span>
                      <span style={{ fontWeight: 700, fontSize: 16 }}>{(creditCycle.data?.consumedTotal ?? 0).toLocaleString()}</span>{' '}
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>
                        deep research {(creditCycle.data?.consumedDeepResearch ?? 0).toLocaleString()} · meeting notes {(creditCycle.data?.consumedMeetingNotes ?? 0).toLocaleString()}
                      </span>
                    </span>
                  ),
                },
                {
                  icon: 'user', label: 'Pool Access',
                  render: () => (
                    <span><span style={{ fontWeight: 700, fontSize: 16 }}>{poolAccessGranted}</span> of {poolAccessTotal} users</span>
                  ),
                },
              ],
            ].map((row, rowIdx) => (
              <Fragment key={rowIdx}>
                {rowIdx > 0 && <div style={{ height: 1, background: '#f3f4f6' }} />}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
                  {row.map((field, colIdx) => {
                    const cell = (
                      <ProfileCell icon={<Icon image={field.icon} color="grey" size={14} />} label={field.label}>
                        {field.render()}
                      </ProfileCell>
                    );
                    return colIdx === 1
                      ? <div key={colIdx} style={{ borderLeft: '1px solid #f3f4f6', borderRight: '1px solid #f3f4f6' }}>{cell}</div>
                      : <Fragment key={colIdx}>{cell}</Fragment>;
                  })}
                </div>
              </Fragment>
            ))}
          </div>

          {/* Bundle history */}
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 8 }}>
              Bundle history
              <span style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af', marginLeft: 8 }}>
                Every bundle ever added to this account — three-month expiry, FIFO
              </span>
            </div>
            <SimpleTable
              columns={[
                {
                  key: 'creditsGranted', label: 'Bundle', dark: true,
                  render: (v, row) => (
                    <div>
                      <div>{v.toLocaleString()} credits{row.priceLabel ? ` · ${row.priceLabel}` : ''}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{row.sourceLabel}</div>
                    </div>
                  ),
                },
                {
                  key: 'statusLabel', label: 'Status',
                  render: (v) => <Badge text={v} color={v === 'Active' ? 'green' : v === 'Expired' ? 'orange' : 'blue'} />,
                },
                {
                  key: 'attributionEmail', label: 'Source',
                  render: (v) => v || <span style={{ color: '#9ca3af' }}>—</span>,
                },
                { key: 'purchasedDate', label: 'Purchased' },
                {
                  key: 'expiresDate', label: 'Expires',
                  render: (v, row) => (
                    <span>
                      {v}{' '}
                      {row.status === 'expired'
                        ? <span style={{ fontSize: 12, fontWeight: 600, color: row.expiredUnused > 0 ? '#dc2626' : '#9ca3af' }}>
                            {row.expiredUnused > 0 ? `expired · ${row.expiredUnused} unused` : 'expired'}
                          </span>
                        : <span style={{ fontSize: 12, fontWeight: 600, color: row.daysRemaining <= 14 ? '#f97316' : '#9ca3af' }}>
                            {row.daysRemaining} days left
                          </span>}
                    </span>
                  ),
                },
                {
                  key: 'remaining', label: 'Used',
                  render: (_, row) => {
                    const used = row.creditsGranted - row.remaining - (row.expiredUnused || 0);
                    const pct = row.creditsGranted > 0 ? Math.round((used / row.creditsGranted) * 100) : 0;
                    return (
                      <div style={{ minWidth: 160 }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 5 }}>{used} of {row.creditsGranted}</div>
                        <div style={{ height: 6, borderRadius: 999, background: '#f3f4f6', overflow: 'hidden' }}>
                          <div style={{ height: 6, background: '#1974f9', width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  },
                },
                { key: 'displayRemaining', label: 'Remaining', dark: true },
              ]}
              rows={bundleRows}
            />
          </div>

          {/* Users, allowances and pool access */}
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 8 }}>
              Users, allowances and pool access
              <span style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af', marginLeft: 8 }}>
                Access is the admin's setting — change it here only with the customer's consent
              </span>
            </div>
            <SimpleTable
              sort={memberSort}
              onSortChange={handleMemberSortChange}
              columns={[
                {
                  key: 'name', label: 'Name', dark: true,
                  render: (v, row) => (
                    <div>
                      <div>{v || row.email || '—'}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                        {row.role ? row.role.charAt(0).toUpperCase() + row.role.slice(1) : '—'}
                      </div>
                    </div>
                  ),
                },
                { key: 'email', label: 'Email' },
                {
                  key: 'tier', label: 'Plan',
                  render: (v) => <LicenseBadge tiers={v ? v.charAt(0).toUpperCase() + v.slice(1) : null} />,
                },
                {
                  key: 'personalRemaining', label: 'Personal Credits',
                  render: (_, row) => !row.tier ? (
                    <span style={{ color: '#9ca3af' }}>no active period</span>
                  ) : row.tier === 'starter' ? (
                    <div>
                      <div>no allowance</div>
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>Starter plan has no allowance</div>
                    </div>
                  ) : (
                    <div>
                      <div>{row.personalRemaining} of {row.personalGranted}</div>
                      {row.periodEnd && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>refills {convertToLocalTime(row.periodEnd).slice(0, 10)}</div>}
                    </div>
                  ),
                },
                {
                  key: 'poolUsedThisCycle', label: 'Pool Used',
                  render: (v) => v > 0 ? v.toLocaleString() : <span style={{ color: '#9ca3af' }}>—</span>,
                },
                {
                  key: 'lastCreditUse', label: 'Last Credit Use', sortKey: 'lastCreditUse',
                  render: (v) => v ? <DateTimeValue iso={v} /> : <span style={{ color: '#9ca3af' }}>—</span>,
                },
                {
                  key: 'isAccountPoolEnabled', label: 'Pool Access',
                  render: (v, row) => (
                    <PoolAccessToggle
                      accountId={id}
                      userId={row.userId}
                      userLabel={row.name ? `${row.name} (${row.email})` : row.email}
                      enabled={v}
                      onChanged={() => creditUsers.refetch()}
                    />
                  ),
                },
                {
                  key: 'actions', label: 'Actions',
                  render: (_, row) => (
                    <button
                      onClick={() => navigate(`/users/${row.userId}`)}
                      style={{ fontSize: 12, color: '#1974f9', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: "'Open Sans', sans-serif" }}
                    >
                      View credits
                    </button>
                  ),
                },
              ]}
              rows={memberRows}
            />
          </div>

          {/* Credit ledger */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Credit ledger</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  placeholder="Search task, user or reference"
                  value={ledgerSearch}
                  onChange={(e) => { setLedgerSearch(e.target.value); setLedgerOffset(0); }}
                  style={{ height: 32, width: 220, padding: '0 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, color: '#374151', outline: 'none', fontFamily: "'Open Sans', sans-serif" }}
                />
                <select
                  value={ledgerEntryType}
                  onChange={(e) => { setLedgerEntryType(e.target.value); setLedgerOffset(0); }}
                  style={{ height: 32, padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer', minWidth: 150 }}
                >
                  <option value="">All events</option>
                  {Object.entries(LEDGER_ENTRY_TYPE_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <select
                  value={ledgerCycle}
                  onChange={(e) => { setLedgerCycle(e.target.value); setLedgerOffset(0); }}
                  style={{ height: 32, padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer' }}
                >
                  <option value="all">All time</option>
                  <option value="current">Current cycle</option>
                </select>
                <button
                  onClick={exportLedgerCsv}
                  disabled={exportingLedger}
                  style={{ height: 32, padding: '0 14px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: exportingLedger ? '#9ca3af' : '#374151', cursor: exportingLedger ? 'default' : 'pointer', fontFamily: "'Open Sans', sans-serif" }}
                >
                  {exportingLedger ? 'Exporting…' : 'Export CSV'}
                </button>
              </div>
            </div>
            {ledgerExportNote && (
              <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8, textAlign: 'right' }}>{ledgerExportNote}</div>
            )}
            <SimpleTable
              columns={[
                {
                  key: 'createdAt', label: 'Timestamp',
                  render: (v) => <DateTimeValue iso={v} />,
                },
                {
                  key: 'entryTypeLabel', label: 'Event', dark: true,
                  render: (v, row) => (
                    <div>
                      <div>{v}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{row.eventLabel}</div>
                    </div>
                  ),
                },
                {
                  key: 'user', label: 'User',
                  render: (v) => v ? (
                    <div>
                      <div>{v.name || v.email}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{v.email}</div>
                    </div>
                  ) : (
                    <div>
                      <div>system</div>
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>automated event</div>
                    </div>
                  ),
                },
                {
                  key: 'drawnFrom', label: 'Drawn From',
                  render: (v) => v || <span style={{ color: '#9ca3af' }}>—</span>,
                },
                {
                  key: 'amount', label: 'Change',
                  render: (v) => v === null || v === undefined
                    ? <span style={{ color: '#9ca3af' }}>—</span>
                    : <span style={{ fontWeight: 600, color: v < 0 ? '#dc2626' : '#16a34a' }}>{v > 0 ? '+' : ''}{v.toLocaleString()}</span>,
                },
                {
                  key: 'poolAfter', label: 'Pool After',
                  render: (v) => v === null || v === undefined ? <span style={{ color: '#9ca3af' }}>—</span> : v.toLocaleString(),
                },
              ]}
              rows={ledgerRows}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 4px' }}>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>
                Pool after is the workspace-pool balance after the event — "—" means the event didn't touch the pool.
              </span>
              {ledgerTotal > LEDGER_LIMIT && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 'none' }}>
                  <span style={{ fontSize: 13, color: '#6b7280' }}>
                    {ledgerOffset + 1}–{Math.min(ledgerOffset + LEDGER_LIMIT, ledgerTotal)} of {ledgerTotal}
                  </span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => setLedgerOffset(Math.max(0, ledgerOffset - LEDGER_LIMIT))}
                      disabled={ledgerOffset === 0}
                      style={{ padding: '4px 12px', fontSize: 13, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: ledgerOffset === 0 ? '#d1d5db' : '#374151', cursor: ledgerOffset === 0 ? 'default' : 'pointer' }}
                    >
                      ‹ Prev
                    </button>
                    <button
                      onClick={() => setLedgerOffset(ledgerOffset + LEDGER_LIMIT)}
                      disabled={ledgerOffset + LEDGER_LIMIT >= ledgerTotal}
                      style={{ padding: '4px 12px', fontSize: 13, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: ledgerOffset + LEDGER_LIMIT >= ledgerTotal ? '#d1d5db' : '#374151', cursor: ledgerOffset + LEDGER_LIMIT >= ledgerTotal ? 'default' : 'pointer' }}
                    >
                      Next ›
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Assigned Plans User List tab ── */}
      {activeTab === 'Assigned Plans User List' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* License summary bar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
            padding: '12px 20px', fontFamily: "'Open Sans', sans-serif",
          }}>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              <span style={{ fontWeight: 600, color: '#111827' }}>{totalPurchased}</span> purchased
              {' · '}
              <span style={{ fontWeight: 600, color: '#111827' }}>{totalAssigned}</span> assigned
              {' · '}
              <span style={{ fontWeight: 600, color: totalAvailable > 0 ? '#16a34a' : '#111827' }}>{totalAvailable}</span> available
            </span>
            <button
              onClick={openAssignModal}
              disabled={totalAvailable <= 0}
              style={{
                padding: '6px 14px', fontSize: 13, fontWeight: 600, borderRadius: 6,
                border: 'none', cursor: totalAvailable > 0 ? 'pointer' : 'default',
                background: totalAvailable > 0 ? '#1974f9' : '#e5e7eb',
                color: totalAvailable > 0 ? '#fff' : '#9ca3af',
                fontFamily: "'Open Sans', sans-serif",
              }}
            >
              Assign License
            </button>
          </div>

          <SimpleTable
            columns={[
              { key: 'name', label: 'Name', dark: true },
              { key: 'email', label: 'Email' },
              { key: 'licenses_display', label: 'Licenses' },
              { key: 'assigned_time', label: 'Assigned Time' },
              { key: 'assigned_by', label: 'Assigned By' },
              {
                key: 'actions', label: 'Actions',
                render: (_, row) => (
                  <button
                    onClick={() => openUnassignModal(row)}
                    style={{ fontSize: 12, color: '#1974f9', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: "'Open Sans', sans-serif" }}
                  >
                    Unassign
                  </button>
                ),
              },
            ]}
            rows={assignedRows}
          />
        </div>
      )}

      {/* ── User List tab ── */}
      {activeTab === 'User List' && (
        <SimpleTable
          columns={[
            { key: 'name', label: 'Name', dark: true },
            { key: 'email', label: 'Email' },
            { key: 'date_created', label: 'Date Created' },
            { key: 'last_active', label: 'Last Active' },
            { key: 'latest_prompts_per_month', label: 'Latest Prompts/mo' },
          ]}
          rows={userRows}
          onRowClick={(row) => navigate(`/users/${row.id}`)}
        />
      )}

      {/* ── Conversations tab ── */}
      {activeTab === 'Conversations' && (
        <AccountConversations email={accountData?.email} />
      )}

      {/* ── Events tab ── */}
      {activeTab === 'Events' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: "'Open Sans', sans-serif" }}>

          {/* Event selector + chart in one card */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: chart ? 20 : 0 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                {eventName ? `${eventName} — events by day` : 'Events by day'}
              </span>
              <select
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                style={{
                  height: 36,
                  padding: '0 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 13,
                  color: '#374151',
                  background: '#fff',
                  cursor: 'pointer',
                  outline: 'none',
                  minWidth: 220,
                }}
              >
                {eventCategories.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {chart && <Chart type="line" data={chart} />}
          </div>

          {/* Events table */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
            {/* Table header */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 220px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              {['Event', 'Time', 'User'].map((h) => (
                <div key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {h}
                </div>
              ))}
            </div>

            {events.loading ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}><Loader /></div>
            ) : !events.data?.list?.results?.length ? (
              <BlankSlateMessage title="No events" text="No events found for this account." />
            ) : (
              events.data.list.results.map((event, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 180px 220px', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
                  <div style={{ padding: '12px 16px', fontSize: 13, color: '#111827', fontWeight: 500 }}>
                    <Badge text={event.name} color="blue" />
                  </div>
                  <div style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>
                    {convertToLocalTime(event.time)}
                  </div>
                  <div style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>
                    {event.user_email || '—'}
                  </div>
                </div>
              ))
            )}

            {/* Pagination */}
            {total > limit && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: 13, color: '#6b7280' }}>
                  {offset + 1}–{Math.min(offset + limit, total)} of {total}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                    style={{ padding: '4px 12px', fontSize: 13, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: offset === 0 ? '#d1d5db' : '#374151', cursor: offset === 0 ? 'default' : 'pointer' }}
                  >
                    ‹ Prev
                  </button>
                  <button
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= total}
                    style={{ padding: '4px 12px', fontSize: 13, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: offset + limit >= total ? '#d1d5db' : '#374151', cursor: offset + limit >= total ? 'default' : 'pointer' }}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* ── Change Owner Modal ── */}
      {showOwnerModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, fontFamily: "'Open Sans', sans-serif" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowOwnerModal(false); }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Change Account Owner</span>
              <button onClick={() => setShowOwnerModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af', lineHeight: 1, padding: 0 }}>✕</button>
            </div>

            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
              Current owner: <span style={{ color: '#111827', fontWeight: 500 }}>{ownerEmail}</span>
            </div>

            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              New owner email
            </label>
            <input
              type="email"
              value={newOwnerEmail}
              onChange={(e) => { setNewOwnerEmail(e.target.value); setOwnerError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && newOwnerEmail && newOwnerEmail !== ownerEmail && !ownerLoading) handleChangeOwner(); }}
              placeholder="member@example.com"
              autoFocus
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, color: '#111827', outline: 'none', marginBottom: 12, fontFamily: "'Open Sans', sans-serif" }}
            />

            {ownerError && (
              <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 12 }}>{ownerError}</div>
            )}

            <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginBottom: 20 }}>
              ⚠ The user must already be a member of this account.
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setShowOwnerModal(false)}
                style={{ padding: '8px 16px', fontSize: 13, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', cursor: 'pointer', fontFamily: "'Open Sans', sans-serif" }}
              >
                Cancel
              </button>
              <button
                onClick={handleChangeOwner}
                disabled={!newOwnerEmail || newOwnerEmail === ownerEmail || ownerLoading}
                style={{ padding: '8px 16px', fontSize: 13, borderRadius: 6, border: 'none', background: (!newOwnerEmail || newOwnerEmail === ownerEmail || ownerLoading) ? '#d1d5db' : '#1974f9', color: '#fff', cursor: (!newOwnerEmail || newOwnerEmail === ownerEmail || ownerLoading) ? 'default' : 'pointer', fontWeight: 600, fontFamily: "'Open Sans', sans-serif" }}
              >
                {ownerLoading ? 'Transferring…' : 'Transfer Ownership'}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── Unassign Modal ── */}
      {showUnassignModal && unassignTarget && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, fontFamily: "'Open Sans', sans-serif" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowUnassignModal(false); }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Unassign Licenses</span>
              <button onClick={() => setShowUnassignModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af', lineHeight: 1, padding: 0 }}>&#10005;</button>
            </div>

            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
              User: <span style={{ color: '#111827', fontWeight: 500 }}>{unassignTarget.email}</span>
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              Select licenses to unassign
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {(unassignTarget.licenses || []).map((lic) => (
                <label key={lic.account_plans_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#111827', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!unassignSelected[lic.account_plans_id]}
                    onChange={(e) => setUnassignSelected((prev) => ({ ...prev, [lic.account_plans_id]: e.target.checked }))}
                    style={{ accentColor: '#1974f9' }}
                  />
                  {lic.name}
                </label>
              ))}
            </div>

            {unassignError && (
              <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 12 }}>{unassignError}</div>
            )}

            <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginBottom: 20 }}>
              The selected licenses will be returned to the available pool.
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setShowUnassignModal(false)}
                style={{ padding: '8px 16px', fontSize: 13, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', cursor: 'pointer', fontFamily: "'Open Sans', sans-serif" }}
              >
                Cancel
              </button>
              <button
                onClick={handleUnassign}
                disabled={!Object.values(unassignSelected).some(Boolean) || unassignLoading}
                style={{
                  padding: '8px 16px', fontSize: 13, borderRadius: 6, border: 'none', fontWeight: 600,
                  background: (!Object.values(unassignSelected).some(Boolean) || unassignLoading) ? '#d1d5db' : '#dc2626',
                  color: '#fff', cursor: (!Object.values(unassignSelected).some(Boolean) || unassignLoading) ? 'default' : 'pointer',
                  fontFamily: "'Open Sans', sans-serif",
                }}
              >
                {unassignLoading ? 'Unassigning…' : 'Unassign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cleanup Unused Licenses Modal ── */}
      {cleanupTarget && (
        <div
          style={MODAL_OVERLAY}
          onClick={(e) => { if (e.target === e.currentTarget) setCleanupTarget(null); }}
        >
          <div style={MODAL_CARD}>
            <div style={MODAL_HEADER_ROW}>
              <span style={MODAL_TITLE}>{CLEANUP_COPY.title}</span>
              <button onClick={() => setCleanupTarget(null)} style={MODAL_CLOSE_BTN}>&#10005;</button>
            </div>

            <div style={{ fontSize: 14, color: '#111827', marginBottom: 16 }}>
              {CLEANUP_COPY.describe({ count: cleanupTarget.available, plan: cleanupTarget.license })}
            </div>

            {/* "Assigned" and "Remaining after removal" are deliberately the same
                number: only unassigned seats go, so the assigned count is exactly
                what survives. Stated explicitly because that is the reassurance
                a rep is looking for before confirming. */}
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
              Purchased <span style={{ color: '#111827', fontWeight: 500 }}>{cleanupTarget.quantity}</span>
              {' · '}Assigned <span style={{ color: '#111827', fontWeight: 500 }}>{cleanupTarget.assigned}</span>
              {' · '}Remaining after removal <span style={{ color: '#111827', fontWeight: 500 }}>{cleanupTarget.assigned}</span>
            </div>

            {cleanupError && <div style={MODAL_ERROR}>{cleanupError}</div>}

            <div style={MODAL_WARNING}>{CLEANUP_COPY.warning}</div>

            <div style={MODAL_FOOTER_ROW}>
              <button onClick={() => setCleanupTarget(null)} style={BTN_SECONDARY}>
                {CLEANUP_COPY.cancel}
              </button>
              <button onClick={handleCleanup} disabled={cleanupLoading} style={btnDestructive(cleanupLoading)}>
                {cleanupLoading ? CLEANUP_COPY.confirming : CLEANUP_COPY.confirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Assign Modal ── */}
      {showAssignModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, fontFamily: "'Open Sans', sans-serif" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowAssignModal(false); }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Assign License</span>
              <button onClick={() => setShowAssignModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af', lineHeight: 1, padding: 0 }}>&#10005;</button>
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              Available licenses
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {availableLicenses.length === 0 && (
                <span style={{ fontSize: 13, color: '#9ca3af' }}>No licenses available</span>
              )}
              {availableLicenses.map((plan) => (
                <label key={plan.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#111827', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!assignSelected[plan.id]}
                    onChange={(e) => setAssignSelected((prev) => ({ ...prev, [plan.id]: e.target.checked }))}
                    style={{ accentColor: '#1974f9' }}
                  />
                  {plan.license} <span style={{ fontSize: 12, color: '#6b7280' }}>({plan.available} available)</span>
                </label>
              ))}
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Assign to
            </div>
            <select
              value={assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '8px 12px',
                border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14,
                color: assignUserId ? '#111827' : '#9ca3af', background: '#fff',
                cursor: 'pointer', marginBottom: 16, fontFamily: "'Open Sans', sans-serif",
              }}
            >
              <option value="" disabled>Select a user...</option>
              {(users.data || []).map((u) => (
                <option key={u.id} value={u.id}>{u.email}{u.name ? ` (${u.name})` : ''}</option>
              ))}
            </select>

            {assignError && (
              <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 12 }}>{assignError}</div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setShowAssignModal(false)}
                style={{ padding: '8px 16px', fontSize: 13, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', cursor: 'pointer', fontFamily: "'Open Sans', sans-serif" }}
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={!Object.values(assignSelected).some(Boolean) || !assignUserId || assignLoading}
                style={{
                  padding: '8px 16px', fontSize: 13, borderRadius: 6, border: 'none', fontWeight: 600,
                  background: (!Object.values(assignSelected).some(Boolean) || !assignUserId || assignLoading) ? '#d1d5db' : '#1974f9',
                  color: '#fff', cursor: (!Object.values(assignSelected).some(Boolean) || !assignUserId || assignLoading) ? 'default' : 'pointer',
                  fontFamily: "'Open Sans', sans-serif",
                }}
              >
                {assignLoading ? 'Assigning…' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

    </Animate>
  );
}
